import React, { useState, useEffect, useRef } from 'react';

const WorkingVoiceAssistant = () => {
 const [isListening, setIsListening] = useState(false);
 const [isSpeaking, setIsSpeaking] = useState(false);
 const [currentLanguage, setCurrentLanguage] = useState('en-US');
 const [transcript, setTranscript] = useState('');
 const [response, setResponse] = useState('');
 const [error, setError] = useState('');
 const [isProcessing, setIsProcessing] = useState(false);
 
 const recognitionRef = useRef(null);

 const languages = [
 { code: 'en-US', name: 'English', flag: '🇺🇸' },
 { code: 'te-IN', name: 'Telugu', flag: '🇮🇳' },
 { code: 'hi-IN', name: 'Hindi', flag: '🇮🇳' }
 ];

 useEffect(() => {
 if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
 const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
 recognitionRef.current = new SpeechRecognition();
 recognitionRef.current.continuous = false;
 recognitionRef.current.interimResults = false;
 recognitionRef.current.lang = currentLanguage;

 recognitionRef.current.onstart = () => {
 console.log('🎤 Started listening');
 setIsListening(true);
 setError('');
 };

 recognitionRef.current.onresult = async (event) => {
 const transcript = event.results[0][0].transcript;
 console.log('🎤 Heard:', transcript);
 setTranscript(transcript);
 setIsListening(false);
 setIsProcessing(true);
 
 try {
 const aiResponse = await getAIResponse(transcript);
 setResponse(aiResponse);
 speak(aiResponse);
 } catch (error) {
 console.error('Error:', error);
 setError('Failed to get response');
 const fallbackResponse = getFallbackResponse(transcript);
 speak(fallbackResponse);
 } finally {
 setIsProcessing(false);
 }
 };

 recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'network') {
        setError('Speech recognition network error. Please check your internet connection.');
        alert("Speech recognition network error. Please check your internet connection or try again later.");
      } else {
        setError('Speech recognition failed: ' + event.error);
      }
    };

 recognitionRef.current.onend = () => {
 console.log('🎤 Stopped listening');
 setIsListening(false);
 };
 } else {
 setError('Speech recognition not supported');
 }
 }, [currentLanguage]);

 const getAIResponse = async (message) => {
 console.log('🤖 Getting AI response for:', message);
 
 const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
 console.log('🔑 API Key exists:', !!apiKey);
 
 if (!apiKey) {
 console.log('⚠️ No API key, using fallback');
 return getFallbackResponse(message);
 }

 const langName = currentLanguage.includes('te') ? 'Telugu' : 
 currentLanguage.includes('hi') ? 'Hindi' : 'English';

 try {
 console.log('🌐 Calling OpenAI API...');
 const response = await fetch('https://api.openai.com/v1/chat/completions', {
 method: 'POST',
 headers: {
 'Authorization': `Bearer ${apiKey}`,
 'Content-Type': 'application/json'
 },
 body: JSON.stringify({
 model: 'gpt-3.5-turbo',
 messages: [
 { 
 role: 'system', 
 content: `You are a helpful assistant. Respond ONLY in ${langName} language. Be concise and helpful.` 
 },
 { role: 'user', content: message }
 ],
 max_tokens: 100,
 temperature: 0.7
 })
 });

 console.log('📡 Response status:', response.status);

 if (response.ok) {
 const data = await response.json();
 const aiResponse = data.choices[0].message.content;
 console.log('✅ OpenAI response:', aiResponse);
 return aiResponse;
 } else {
 const errorText = await response.text();
 console.error('❌ OpenAI API error:', response.status, errorText);
 return getFallbackResponse(message);
 }
 } catch (error) {
 console.error('❌ OpenAI API error:', error);
 return getFallbackResponse(message);
 }
 };

 const getFallbackResponse = (message) => {
 console.log('🔄 Using fallback response');
 const lowerMessage = message.toLowerCase();
 
 if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('హలో') || lowerMessage.includes('नमस्ते')) {
 return currentLanguage.includes('te') ? 'హలో! నేను మీకు సహాయం చేయగలను.' :
 currentLanguage.includes('hi') ? 'नमस्ते! मैं आपकी मदद कर सकता हूं।' :
 'Hello! I can help you.';
 }
 
 if (lowerMessage.includes('python') || lowerMessage.includes('పైథాన్') || lowerMessage.includes('पायथन')) {
 return currentLanguage.includes('te') ? 'పైథాన్ ఒక ప్రోగ్రామింగ్ భాష.' :
 currentLanguage.includes('hi') ? 'पायथन एक प्रोग्रामिंग भाषा है।' :
 'Python is a programming language.';
 }
 
 if (lowerMessage.includes('help') || lowerMessage.includes('సహాయం') || lowerMessage.includes('मदद')) {
 return currentLanguage.includes('te') ? 'నేను మీకు సహాయం చేయగలను.' :
 currentLanguage.includes('hi') ? 'मैं आपकी मदद कर सकता हूं।' :
 'I can help you.';
 }
 
 return currentLanguage.includes('te') ? 'నేను మీ ప్రశ్నను విన్నాను.' :
 currentLanguage.includes('hi') ? 'मैंने आपका सवाल सुना है।' :
 'I heard your question.';
 };

 const speak = (text) => {
 console.log('🔊 Speaking:', text);
 if ('speechSynthesis' in window) {
 window.speechSynthesis.cancel();
 const utterance = new SpeechSynthesisUtterance(text);
 utterance.lang = currentLanguage;
 utterance.rate = 0.9;
 utterance.pitch = 1;
 
 utterance.onstart = () => {
 console.log('🔊 Started speaking');
 setIsSpeaking(true);
 };
 
 utterance.onend = () => {
 console.log('🔊 Finished speaking');
 setIsSpeaking(false);
 };
 
 utterance.onerror = (event) => {
 console.error('🔊 Speech synthesis error:', event.error);
 setIsSpeaking(false);
 };
 
 window.speechSynthesis.speak(utterance);
 }
 };

 const startListening = () => {
 if (recognitionRef.current && !isListening && !isSpeaking && !isProcessing) {
 console.log('🎤 Starting speech recognition...');
 recognitionRef.current.start();
 }
 };

 const changeLanguage = (langCode) => {
 setCurrentLanguage(langCode);
 if (recognitionRef.current) {
 recognitionRef.current.lang = langCode;
 }
 console.log('🌍 Language changed to:', langCode);
 };

 const testMessage = async (message) => {
 setTranscript(message);
 setIsProcessing(true);
 try {
 const aiResponse = await getAIResponse(message);
 setResponse(aiResponse);
 speak(aiResponse);
 } catch (error) {
 console.error('Test error:', error);
 setError('Test failed');
 } finally {
 setIsProcessing(false);
 }
 };

 return (
 <div className="min-h-screen bg-bgSection p-4">
 <div className="max-w-2xl mx-auto">
 <h1 className="text-3xl font-bold text-white text-center mb-8">
 🤖 Working Voice Assistant
 </h1>

 {/* Debug Info */}
 <div className="bg-bgSection p-4 rounded-lg mb-6">
 <h3 className="text-white mb-2">🔧 Debug Info</h3>
 <div className="text-sm text-muted space-y-1">
 <p>API Key: {process.env.REACT_APP_OPENAI_API_KEY ? '✅ Present' : '❌ Missing'}</p>
 <p>Speech Recognition: {'webkitSpeechRecognition' in window || 'SpeechRecognition' in window ? '✅ Supported' : '❌ Not Supported'}</p>
 <p>Speech Synthesis: {'speechSynthesis' in window ? '✅ Supported' : '❌ Not Supported'}</p>
 <p>Current Language: {currentLanguage}</p>
 </div>
 </div>

 {/* Language Selector */}
 <div className="bg-bgSection p-6 rounded-lg mb-6">
 <h3 className="text-white mb-4">🌍 Select Language</h3>
 <div className="flex gap-3">
 {languages.map((lang) => (
 <button
 key={lang.code}
 onClick={() => changeLanguage(lang.code)}
 className={`px-4 py-2 rounded ${
 currentLanguage === lang.code
 ? 'bg-blue-500 text-white'
 : 'bg-gray-700 text-white hover:bg-gray-600'
 }`}
 >
 {lang.flag} {lang.name}
 </button>
 ))}
 </div>
 </div>

 {/* Voice Control */}
 <div className="bg-bgSection p-6 rounded-lg mb-6 text-center">
 <button
 onClick={startListening}
 disabled={isListening || isSpeaking || isProcessing}
 className={`w-20 h-20 rounded-full text-3xl ${
 isListening
 ? 'bg-red-500 animate-pulse'
 : 'bg-green-500 hover:bg-green-600'
 } text-white ${isSpeaking || isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
 >
 🎤
 </button>
 
 <p className="text-white mt-4">
 {isListening && '🎤 Listening...'}
 {isProcessing && '⚙️ Processing...'}
 {isSpeaking && '🔊 Speaking...'}
 {!isListening && !isSpeaking && !isProcessing && 'Click to speak'}
 </p>
 </div>

 {/* Test Buttons */}
 <div className="bg-bgSection p-6 rounded-lg mb-6">
 <h3 className="text-white mb-4">🧪 Test Buttons</h3>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 <button
 onClick={() => testMessage('Hello, how are you?')}
 disabled={isSpeaking || isProcessing}
 className="p-3 bg-blue-500 hover:bg-blue-600 text-white rounded disabled:opacity-50"
 >
 Hello
 </button>
 <button
 onClick={() => testMessage('What is Python?')}
 disabled={isSpeaking || isProcessing}
 className="p-3 bg-green-500 hover:bg-green-600 text-white rounded disabled:opacity-50"
 >
 What is Python?
 </button>
 <button
 onClick={() => testMessage('Tell me a joke')}
 disabled={isSpeaking || isProcessing}
 className="p-3 bg-purple-500 hover:bg-purple-600 text-white rounded disabled:opacity-50"
 >
 Tell me a joke
 </button>
 </div>
 </div>

 {/* Messages */}
 {transcript && (
 <div className="bg-bgSection p-4 rounded-lg mb-4">
 <h4 className="text-white mb-2">🎤 You said:</h4>
 <p className="text-muted">{transcript}</p>
 </div>
 )}

 {response && (
 <div className="bg-bgSection p-4 rounded-lg mb-4">
 <h4 className="text-white mb-2">🤖 Assistant replied:</h4>
 <p className="text-muted">{response}</p>
 </div>
 )}

 {error && (
 <div className="bg-red-900 p-4 rounded-lg mb-4">
 <h4 className="text-red-200 mb-2">❌ Error:</h4>
 <p className="text-red-300">{error}</p>
 </div>
 )}

 {/* Setup Instructions */}
 <div className="bg-primary p-4 rounded-lg">
 <h3 className="text-primary font-bold mb-2">⚙️ Setup</h3>
 <p className="text-primary text-sm">
 Make sure your .env file contains: <code>REACT_APP_OPENAI_API_KEY=your_key</code>
 </p>
 </div>
 </div>
 </div>
 );
};

export default WorkingVoiceAssistant;








